import { ConfirmActionModal } from '@/shared/ui/confirm-action-modal.component';
import { Button, Field } from '@noorden-insurtech/alba-design-system';
import { FileText, Upload, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

/** Mesmo limite do `FileInterceptor` da rota de emissão manual. */
const MAX_POLICY_PDF_SIZE = 10 * 1024 * 1024;

interface ManualEmissionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (i4proPolicyNo: string, policyPdf: File | null) => void;
  isLoading: boolean;
  /** Erro devolvido pelo BFF, exibido no próprio modal. */
  errorMessage?: string | null;
  proposalNo?: string;
  /**
   * Pede o PDF da apólice emitida no i4Pro, obrigatório. Desligado no RD
   * Equipamentos, que não tem PDF nem ECM no Portal.
   */
  requiresPolicyPdf?: boolean;
}

function formatFileSize(bytes: number): string {
  const mb = bytes / (1024 * 1024);
  if (mb >= 1) return `${mb.toFixed(mb < 10 ? 1 : 0)} MB`;
  return `${(bytes / 1024).toFixed(0)} KB`;
}

/** Devolve a mensagem de erro, ou `undefined` quando o arquivo serve. */
function validatePolicyPdf(file: File): string | undefined {
  const isPdf =
    file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
  if (!isPdf) return 'O arquivo da apólice precisa ser um PDF.';
  if (file.size > MAX_POLICY_PDF_SIZE)
    return 'O PDF da apólice pode ter no máximo 10 MB.';
  return undefined;
}

/**
 * Popup do "Emitir manualmente": pede o nº da apólice já emitida no i4Pro e o
 * PDF dela — o Portal não gera o documento na emissão manual; o PDF enviado é
 * o que fica na apólice e vai ao ECM.
 *
 * O modal NÃO fecha em caso de erro — a mensagem do BFF (apólice inexistente, de
 * outro ramo/produto, já vinculada a outra proposta, i4Pro indisponível) aparece
 * aqui dentro e o administrador corrige sem redigitar nem reanexar nada.
 */
export function ManualEmissionModal({
  isOpen,
  onClose,
  onConfirm,
  isLoading,
  errorMessage,
  proposalNo,
  requiresPolicyPdf = true,
}: ManualEmissionModalProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [policyPdf, setPolicyPdf] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  // Reabrir não herda o arquivo de uma tentativa anterior.
  useEffect(() => {
    if (isOpen) {
      setPolicyPdf(null);
      setFileError(null);
    }
  }, [isOpen]);

  const handleFile = (files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;

    const error = validatePolicyPdf(file);
    setFileError(error ?? null);
    setPolicyPdf(error ? null : file);
  };

  const removeFile = () => {
    setPolicyPdf(null);
    setFileError(null);
    // Permite escolher o mesmo arquivo de novo (o `change` não dispara sem isso).
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <ConfirmActionModal
      isOpen={isOpen}
      onClose={onClose}
      onConfirm={i4proPolicyNo => onConfirm(i4proPolicyNo, policyPdf)}
      isLoading={isLoading}
      errorMessage={errorMessage}
      isExtraValid={!requiresPolicyPdf || policyPdf !== null}
      title='Emitir manualmente'
      confirmLabel='Confirmar emissão'
      description={
        <>
          Informe o número da apólice que já foi emitida no i4Pro
          {proposalNo ? (
            <>
              {' '}
              para a proposta{' '}
              <span className='font-semibold text-alba-black-500'>
                {proposalNo}
              </span>
            </>
          ) : null}
          {requiresPolicyPdf ? ' e anexe o PDF dela' : null}. O Portal vai
          validar o número no i4Pro e, estando tudo certo, concluir a emissão
          desta proposta.
        </>
      }
      warning={
        <>
          Esta ação é <strong>irreversível</strong>: vincula a apólice a esta
          proposta e conclui a emissão.
        </>
      }
      valuePrompt={{
        label: 'Número da apólice no i4Pro',
        placeholder: 'Ex.: 100014000012345',
        inputMode: 'numeric',
        validate: value =>
          /^\d+$/.test(value)
            ? undefined
            : 'O número da apólice deve conter apenas dígitos.',
      }}
    >
      {requiresPolicyPdf && (
        <Field.Root invalid={!!fileError}>
          <Field.Label>
            PDF da apólice
            <span className='text-alba-error-red-500'>*</span>
          </Field.Label>

          {policyPdf ? (
            <div className='flex items-center gap-2 overflow-hidden rounded-xl border border-alba-gray-400 bg-white p-2'>
              <FileText
                className='size-6 shrink-0 text-alba-black-500'
                strokeWidth={1.6}
              />
              <div className='flex min-w-0 flex-1 flex-col'>
                <span className='truncate font-montserrat text-sm font-bold text-alba-black-500'>
                  {policyPdf.name}
                </span>
                <span className='font-montserrat text-xs text-alba-gray-600'>
                  {formatFileSize(policyPdf.size)}
                </span>
              </div>
              <button
                type='button'
                aria-label='Remover PDF da apólice'
                onClick={removeFile}
                disabled={isLoading}
                className='shrink-0 text-alba-black-500 transition-colors hover:text-alba-error-red-500 disabled:cursor-not-allowed'
              >
                <X className='size-4' strokeWidth={1.6} />
              </button>
            </div>
          ) : (
            <div
              onDrop={event => {
                event.preventDefault();
                if (!isLoading) handleFile(event.dataTransfer.files);
              }}
              onDragOver={event => event.preventDefault()}
            >
              <Button
                variant='secondary'
                size='md'
                disabled={isLoading}
                onClick={() => inputRef.current?.click()}
              >
                <Upload className='size-5' strokeWidth={1.6} />
                Anexar PDF
              </Button>
            </div>
          )}

          <input
            ref={inputRef}
            type='file'
            accept='application/pdf,.pdf'
            className='hidden'
            onChange={event => handleFile(event.target.files)}
          />

          {fileError ? (
            <Field.ErrorText>{fileError}</Field.ErrorText>
          ) : (
            <Field.HelperText>
              A apólice emitida no i4Pro, em PDF (até 10 MB). Fica nos
              documentos da apólice e vai para o ECM.
            </Field.HelperText>
          )}
        </Field.Root>
      )}
    </ConfirmActionModal>
  );
}
